<script>
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { mapGetters } from 'vuex';

export default {
  components: {
    Thumbnail,
    Spinner,
    EmptyState,
  },
  props: {
    notifications: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isUpdating: {
      type: Boolean,
      default: false,
    },
    onClickNotification: {
      type: Function,
      default: () => {},
    },
    onMarkAllDoneClick: {
      type: Function,
      default: () => {},
    },
  },
  computed: {
    ...mapGetters({
      notificationMetadata: 'notifications/getMeta',
    }),
    showEmptyResult() {
      return !this.isLoading && this.notifications.length === 0;
    },
    typeIconMap() {
      return {
        conversation_mention: 'at-sign',
        conversation_assignment: 'message-circle-code',
        assigned_conversation_new_message: 'message-circle',
        participating_conversation_new_message: 'message-circle',
        conversation_creation: 'message-circle-plus',
        sla_missed_first_response: 'triangle-alert',
        sla_missed_next_response: 'triangle-alert',
        sla_missed_resolution: 'triangle-alert',
        zalo_personal_disconnected: 'message-circle-question-mark',
        ai_reply_quota_alert: 'message-circle-warning',
        channel_message_failed: 'message-circle-x',
      };
    },
  },
  methods: {
    dynamicTime,
    typeIcon(type) {
      return this.typeIconMap[type] || 'chat';
    },
  },
};
</script>

<template>
  <section
    class="flex-grow flex-shrink h-full px-4 py-8 overflow-hidden bg-white dark:bg-slate-900"
  >
    <woot-submit-button
      v-if="notificationMetadata.unreadCount"
      class="rounded-lg input-base !bg-grass-500 flex items-center gap-2 text-white text-sm font-semibold fixed top-4 ltr:right-4 rtl:left-4 lg:ltr:right-6 lg:rtl:left-6 z-10"
      :button-text="$t('NOTIFICATIONS_PAGE.MARK_ALL_DONE')"
      :loading="isUpdating"
      @click="onMarkAllDoneClick"
    />

    <!-- Mobile: Card list -->
    <div class="block md:hidden">
      <div v-show="!isLoading" class="flex flex-col gap-2">
        <article
          v-for="notificationItem in notifications"
          :key="notificationItem.id"
          class="relative rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 p-3 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700/60 transition"
          @click="() => onClickNotification(notificationItem)"
        >
          <div
            v-if="!notificationItem.read_at"
            class="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-hichat-500"
          />
          <div class="flex items-start gap-3">
            <div class="pt-0.5">
              <Thumbnail
                v-if="notificationItem.primary_actor.meta.assignee"
                :src="notificationItem.primary_actor.meta.assignee.thumbnail"
                size="28px"
                :username="notificationItem.primary_actor.meta.assignee.name"
              />
              <div
                v-else
                class="flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
              >
                <lucide-icon :name="typeIcon(notificationItem.notification_type)" size="16" />
              </div>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2 flex-wrap">
                <h5
                  class="notification--title"
                  :class="{ 'font-semibold': notificationItem.read_at === null }"
                >
                  {{
                    `#${
                      notificationItem.primary_actor
                        ? notificationItem.primary_actor.id
                        : $t(`NOTIFICATIONS_PAGE.DELETE_TITLE`)
                    }`
                  }}
                </h5>
                <span
                  v-if="notificationItem.inbox_name"
                  class="text-xxs p-0.5 px-1.5 bg-n-gray-1 dark:bg-n-gray-3 text-hilab dark:text-hilab rounded-md font-medium"
                  :title="notificationItem.inbox_name"
                >
                  {{ notificationItem.inbox_name }}
                </span>
              </div>
              <div class="flex flex-col gap-1 max-w-full">
                <span class="notification--message-title truncate">
                  {{ notificationItem.push_message_title }}
                </span>
                <span
                  v-if="notificationItem.push_message_body"
                  class="text-slate-600 dark:text-slate-300 text-xs truncate"
                >
                  {{ notificationItem.push_message_body }}
                </span>
              </div>
              <div class="mt-2 flex items-center justify-between">
                <span class="notification--type">
                  {{
                    $t(
                      `NOTIFICATIONS_PAGE.TYPE_LABEL.${notificationItem.notification_type}`
                    )
                  }}
                </span>
                <span class="notification--created-at">
                  {{ dynamicTime(notificationItem.last_activity_at) }}
                </span>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>

    <!-- Desktop/Tablet: Table view -->
    <div class="hidden md:block">
      <table class="woot-table notifications-table">
        <tbody v-show="!isLoading">
          <tr
            v-for="notificationItem in notifications"
            :key="notificationItem.id"
            :class="{
              'is-unread': notificationItem.read_at === null,
            }"
            @click="() => onClickNotification(notificationItem)"
          >
            <td>
              <div
                class="overflow-hidden flex-view notification-contant--wrap whitespace-nowrap text-ellipsis"
              >
                <div class="flex items-center gap-2 flex-wrap">
                  <div
                    v-if="!notificationItem.primary_actor.meta.assignee"
                    class="flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                  >
                    <lucide-icon :name="typeIcon(notificationItem.notification_type)" size="16" />
                  </div>
                  <h5 class="notification--title">
                    {{
                      `#${
                        notificationItem.primary_actor
                          ? notificationItem.primary_actor.id
                          : $t(`NOTIFICATIONS_PAGE.DELETE_TITLE`)
                      }`
                    }}
                  </h5>
                  <span
                    v-if="notificationItem.inbox_name"
                    class="text-xxs p-0.5 px-1.5 bg-n-gray-1 dark:bg-n-gray-3 text-hilab dark:text-hilab rounded-md font-medium"
                    :title="notificationItem.inbox_name"
                  >
                    {{ notificationItem.inbox_name }}
                  </span>
                </div>
                <div class="flex flex-col gap-1 max-w-full">
                  <span
                    class="overflow-hidden notification--message-title whitespace-nowrap text-ellipsis"
                  >
                    {{ notificationItem.push_message_title }}
                  </span>
                  <span
                    v-if="notificationItem.push_message_body"
                    class="overflow-hidden text-slate-600 dark:text-slate-300 text-xs whitespace-nowrap text-ellipsis"
                  >
                    {{ notificationItem.push_message_body }}
                  </span>
                </div>
              </div>
            </td>
            <td class="text-right">
              <span class="notification--type">
                {{
                  $t(
                    `NOTIFICATIONS_PAGE.TYPE_LABEL.${notificationItem.notification_type}`
                  )
                }}
              </span>
            </td>
            <td class="thumbnail--column">
              <Thumbnail
                v-if="notificationItem.primary_actor.meta.assignee"
                :src="notificationItem.primary_actor.meta.assignee.thumbnail"
                size="36px"
                :username="notificationItem.primary_actor.meta.assignee.name"
              />
            </td>
            <td>
              <div class="text-right timestamp--column">
                <span class="notification--created-at">
                  {{ dynamicTime(notificationItem.last_activity_at) }}
                </span>
              </div>
            </td>
            <td>
              <div
                v-if="!notificationItem.read_at"
                class="notification--unread-indicator"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <EmptyState
      v-if="showEmptyResult"
      :title="$t('NOTIFICATIONS_PAGE.LIST.404')"
    />
    <div v-if="isLoading" class="notifications--loader">
      <Spinner />
      <span>{{ $t('NOTIFICATIONS_PAGE.LIST.LOADING_MESSAGE') }}</span>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import 'dashboard/assets/scss/mixins';

.notification--title {
  @apply text-sm m-0 text-slate-800 dark:text-slate-100;
}

.notifications-table {
  > tbody {
    > tr {
      @apply cursor-pointer;

      &:hover {
        @apply bg-slate-50 dark:bg-slate-800;
      }

      &.is-active {
        @apply bg-slate-100 dark:bg-slate-700;
      }

      > td {
        &.conversation-count-item {
          @apply pl-6 rtl:pl-0 rtl:pr-6;
        }
      }

      &:last-child {
        @apply border-b-0;
      }
    }
  }
}

.is-unread {
  @apply font-semibold;
}

.notifications--loader {
  @apply text-base flex items-center justify-center p-10;
}

.notification--unread-indicator {
  @apply w-2.5 h-2.5 rounded-full bg-hichat-500 dark:bg-hichat-500;
}

.notification--created-at {
  @apply text-slate-700 dark:text-slate-200 text-xs;
}

.notification--type {
  @apply text-xs;
}

.thumbnail--column {
  @apply w-[3.25rem];
}

.timestamp--column {
  @apply min-w-[5rem] text-right;
}

.notification-contant--wrap {
  @apply flex-col max-w-[31.25rem];
}

.notification--message-title {
  @apply text-slate-700 dark:text-slate-100;
}
</style>
